import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { 
  Target, Plus, Search, Users, TrendingUp, DollarSign, 
  Calendar, MoreHorizontal, Eye, Edit, Trash2, UserPlus,
  BarChart3, ArrowUpRight, ArrowDownRight, Clock, CheckCircle,
  XCircle, Pause, Play, Filter, Download, Brain, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { campaignsAPI, prospectsAPI, aiWorkforceAPI } from '../api';
import { format, formatDistanceToNow } from 'date-fns';

export default function SalesCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignProspects, setCampaignProspects] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, paused: 0, completed: 0, totalProspects: 0, aiGenerated: 0 });
  
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    campaign_type: 'email',
    channels: [],
    budget: null,
    target_segments: [],
    target_product_lines: [],
    tags: []
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const res = await campaignsAPI.getAll();
      const campaignsList = res.data.campaigns || res.data || [];
      setCampaigns(campaignsList);
      
      // Calculate stats - use 'live' status instead of 'active'
      const active = campaignsList.filter(c => c.status === 'live' || c.status === 'active').length;
      const paused = campaignsList.filter(c => c.status === 'paused').length;
      const completed = campaignsList.filter(c => c.status === 'completed').length;
      const totalProspects = campaignsList.reduce((sum, c) => sum + (c.prospect_count || 0), 0);
      const aiGenerated = campaignsList.reduce((sum, c) => sum + (c.ai_prospect_count || 0), 0);
      
      setStats({
        total: campaignsList.length,
        active,
        paused,
        completed,
        totalProspects,
        aiGenerated
      });
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignProspects = async (campaignId) => {
    try {
      const res = await prospectsAPI.getProspects({ campaign_id: campaignId });
      setCampaignProspects(res.data.prospects || res.data || []);
    } catch (error) {
      console.error('Failed to fetch campaign prospects:', error);
      setCampaignProspects([]);
    }
  };

  const handleCreateCampaign = async () => {
    if (!newCampaign.name) {
      toast.error('Campaign name is required');
      return;
    }
    
    try {
      // Ensure budget is a number or null
      const campaignData = {
        ...newCampaign,
        budget: newCampaign.budget ? parseFloat(newCampaign.budget) : null
      };
      await campaignsAPI.create(campaignData);
      toast.success('Campaign created successfully');
      setShowCreateDialog(false);
      setNewCampaign({ 
        name: '', 
        description: '', 
        campaign_type: 'email', 
        channels: [], 
        budget: null, 
        target_segments: [], 
        target_product_lines: [], 
        tags: [] 
      });
      fetchCampaigns();
    } catch (error) {
      console.error('Campaign creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const handleViewCampaign = async (campaign) => {
    setSelectedCampaign(campaign);
    await fetchCampaignProspects(campaign.id);
    setShowDetailDialog(true);
  };

  const handleUpdateStatus = async (campaignId, newStatus) => {
    try {
      await campaignsAPI.update(campaignId, { status: newStatus });
      toast.success(`Campaign ${newStatus}`);
      fetchCampaigns();
      if (selectedCampaign?.id === campaignId) {
        setSelectedCampaign({ ...selectedCampaign, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update campaign status');
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await campaignsAPI.delete(campaignId);
      toast.success('Campaign deleted');
      fetchCampaigns();
      if (selectedCampaign?.id === campaignId) {
        setShowDetailDialog(false);
      }
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': 
      case 'live': return 'bg-emerald-100 text-emerald-800';
      case 'paused': return 'bg-amber-100 text-amber-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
      case 'live': return <Play className="h-3 w-3" />;
      case 'paused': return <Pause className="h-3 w-3" />;
      case 'completed': return <CheckCircle className="h-3 w-3" />;
      default: return <Clock className="h-3 w-3" />;
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="sales-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Target className="h-6 w-6 text-violet-600" />
            Sales Campaigns
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage campaigns and track AI-generated prospects
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-campaign-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-violet-600 font-medium">Total Campaigns</p>
                <p className="text-2xl font-bold text-violet-700">{stats.total}</p>
              </div>
              <Target className="h-8 w-8 text-violet-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 font-medium">Active</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.active}</p>
              </div>
              <Play className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Paused</p>
                <p className="text-2xl font-bold text-amber-700">{stats.paused}</p>
              </div>
              <Pause className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Completed</p>
                <p className="text-2xl font-bold text-blue-700">{stats.completed}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-600 font-medium">Total Prospects</p>
                <p className="text-2xl font-bold text-slate-700">{stats.totalProspects}</p>
              </div>
              <Users className="h-8 w-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-pink-600 font-medium">AI Generated</p>
                <p className="text-2xl font-bold text-pink-700">{stats.aiGenerated}</p>
              </div>
              <Brain className="h-8 w-8 text-pink-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-campaigns"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Campaigns Grid */}
      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading campaigns...</div>
      ) : filteredCampaigns.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Campaigns Found</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'Try adjusting your filters' 
                : 'Create your first campaign to start tracking prospects'}
            </p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCampaigns.map((campaign) => (
            <Card 
              key={campaign.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleViewCampaign(campaign)}
              data-testid={`campaign-card-${campaign.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      {campaign.name}
                    </CardTitle>
                    <CardDescription className="line-clamp-2 mt-1">
                      {campaign.description || 'No description'}
                    </CardDescription>
                  </div>
                  <Badge className={getStatusColor(campaign.status)}>
                    {getStatusIcon(campaign.status)}
                    <span className="ml-1 capitalize">{campaign.status}</span>
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-2 bg-slate-50 rounded">
                    <p className="text-xl font-bold text-slate-700">{campaign.prospect_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Prospects</p>
                  </div>
                  <div className="text-center p-2 bg-pink-50 rounded">
                    <p className="text-xl font-bold text-pink-700">{campaign.ai_prospect_count || 0}</p>
                    <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                      <Sparkles className="h-3 w-3" /> AI Generated
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {campaign.created_at ? formatDistanceToNow(new Date(campaign.created_at), { addSuffix: true }) : 'N/A'}
                  </span>
                  <Badge variant="outline" className="text-xs capitalize">
                    {campaign.type || 'outbound'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Create New Campaign
            </DialogTitle>
            <DialogDescription>
              Set up a new sales campaign to organize and track prospects
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Campaign Name *</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({...newCampaign, name: e.target.value})}
                placeholder="e.g., Q1 Enterprise Outreach"
                data-testid="campaign-name-input"
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({...newCampaign, description: e.target.value})}
                placeholder="Describe the campaign goals and strategy..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Campaign Type *</Label>
                <Select 
                  value={newCampaign.campaign_type} 
                  onValueChange={(val) => setNewCampaign({...newCampaign, campaign_type: val})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="social_media">Social Media</SelectItem>
                    <SelectItem value="paid_ads">Paid Ads</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="content">Content</SelectItem>
                    <SelectItem value="multi_channel">Multi-Channel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Budget</Label>
                <Input
                  type="number"
                  value={newCampaign.budget || ''}
                  onChange={(e) => setNewCampaign({...newCampaign, budget: e.target.value})}
                  placeholder="e.g., 5000"
                />
              </div>
            </div>

            <div>
              <Label>Channels</Label>
              <Select 
                value={newCampaign.channels?.[0] || ''} 
                onValueChange={(val) => setNewCampaign({...newCampaign, channels: val ? [val] : []})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="twitter">Twitter</SelectItem>
                  <SelectItem value="google_ads">Google Ads</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateCampaign}
              disabled={!newCampaign.name}
              data-testid="submit-campaign-btn"
            >
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedCampaign && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="text-xl flex items-center gap-2">
                      {selectedCampaign.name}
                      <Badge className={getStatusColor(selectedCampaign.status)}>
                        {getStatusIcon(selectedCampaign.status)}
                        <span className="ml-1 capitalize">{selectedCampaign.status}</span>
                      </Badge>
                    </DialogTitle>
                    <DialogDescription className="mt-1">
                      {selectedCampaign.description || 'No description provided'}
                    </DialogDescription>
                  </div>
                  <div className="flex gap-2">
                    {selectedCampaign.status === 'active' && (
                      <Button variant="outline" size="sm" onClick={() => handleUpdateStatus(selectedCampaign.id, 'paused')}>
                        <Pause className="h-4 w-4 mr-1" /> Pause
                      </Button>
                    )}
                    {selectedCampaign.status === 'paused' && (
                      <Button variant="outline" size="sm" onClick={() => handleUpdateStatus(selectedCampaign.id, 'active')}>
                        <Play className="h-4 w-4 mr-1" /> Resume
                      </Button>
                    )}
                    {selectedCampaign.status !== 'completed' && (
                      <Button variant="outline" size="sm" onClick={() => handleUpdateStatus(selectedCampaign.id, 'completed')}>
                        <CheckCircle className="h-4 w-4 mr-1" /> Complete
                      </Button>
                    )}
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteCampaign(selectedCampaign.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </DialogHeader>

              {/* Campaign Stats */}
              <div className="grid grid-cols-4 gap-4 my-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-700">{selectedCampaign.prospect_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Prospects</p>
                </div>
                <div className="text-center p-3 bg-pink-50 rounded-lg">
                  <p className="text-2xl font-bold text-pink-700">{selectedCampaign.ai_prospect_count || 0}</p>
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Sparkles className="h-3 w-3" /> AI Generated
                  </p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-700">{selectedCampaign.conversion_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Conversions</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">
                    {selectedCampaign.prospect_count > 0 
                      ? Math.round((selectedCampaign.conversion_count || 0) / selectedCampaign.prospect_count * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-muted-foreground">Conversion Rate</p>
                </div>
              </div>

              {/* Campaign Details */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Type:</span>
                    <Badge variant="outline" className="capitalize">{selectedCampaign.type || 'outbound'}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Target:</span>
                    <span>{selectedCampaign.target_audience || 'Not specified'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Created:</span>
                    <span>{selectedCampaign.created_at ? format(new Date(selectedCampaign.created_at), 'MMM d, yyyy') : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Goals:</span>
                    <span className="line-clamp-1">{selectedCampaign.goals || 'Not specified'}</span>
                  </div>
                </div>
              </div>

              {/* Prospects Table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Campaign Prospects ({campaignProspects.length})
                  </h3>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
                
                {campaignProspects.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground border rounded-lg">
                    <Users className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p>No prospects in this campaign yet</p>
                    <p className="text-xs mt-1">Use AI Workforce to generate and import prospects</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Company</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Source</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {campaignProspects.map((prospect) => (
                          <TableRow key={prospect.id}>
                            <TableCell className="font-medium">{prospect.name || prospect.contact_name || 'N/A'}</TableCell>
                            <TableCell>{prospect.company || prospect.company_name || 'N/A'}</TableCell>
                            <TableCell>{prospect.email || 'N/A'}</TableCell>
                            <TableCell>
                              {prospect.campaign_source === 'ai_import' ? (
                                <Badge variant="outline" className="bg-pink-50 text-pink-700">
                                  <Sparkles className="h-3 w-3 mr-1" /> AI
                                </Badge>
                              ) : (
                                <Badge variant="outline">Manual</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {prospect.status || 'new'}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
